import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// ── Constants ────────────────────────────────────────────────────────────────

const INDUSTRIES = [
  'HVAC / Field Services',
  'Healthcare / Dental',
  'Legal / Compliance',
  'Real Estate',
  'Finance / DeFi',
] as const;

const TASK_OPTIONS = [
  { id: 'schedule', label: 'Schedule Appts' },
  { id: 'calls', label: 'Answer Calls' },
  { id: 'invoices', label: 'Send Invoices' },
  { id: 'support', label: 'Tier 1 Support' },
  { id: 'leads', label: 'Qualify Leads' },
];

// ── Page component ───────────────────────────────────────────────────────────

export function ProBuildWizardPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [industry, setIndustry] = useState('');
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [businessName, setBusinessName] = useState('');
  const [hoursStart, setHoursStart] = useState('09:00');
  const [hoursEnd, setHoursEnd] = useState('17:00');

  // Step 5 auto-advance
  useEffect(() => {
    if (step !== 5) return;
    const timer = setTimeout(() => setStep(6), 3000);
    return () => clearTimeout(timer);
  }, [step]);

  const toggleTask = useCallback((id: string) => {
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Step 1: Industry ──────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="space-y-4">
      <label className="block text-xs font-bold" style={{ color: 'var(--text-secondary)' }}>
        1. What Industry?
      </label>
      <select
        value={industry}
        onChange={e => setIndustry(e.target.value)}
        className="w-full border p-2 text-sm"
        style={{ borderColor: '#d1d5db', background: 'white' }}
      >
        <option value="">-- Select --</option>
        {INDUSTRIES.map(ind => (
          <option key={ind} value={ind}>{ind}</option>
        ))}
      </select>
      <button
        disabled={!industry}
        onClick={() => setStep(2)}
        className="w-full font-bold py-2 text-xs disabled:opacity-40"
        style={{ background: '#3b5998', color: 'white' }}
      >
        Next: Capabilities
      </button>
    </div>
  );

  // ── Step 2: Tasks ─────────────────────────────────────────────────────────

  const renderStep2 = () => (
    <div className="space-y-4">
      <label className="block text-xs font-bold" style={{ color: 'var(--text-secondary)' }}>
        2. What should your agent do?
      </label>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {TASK_OPTIONS.map(task => (
          <label
            key={task.id}
            className="p-2 border rounded flex items-center gap-2"
            style={{ borderColor: '#d1d5db' }}
          >
            <input
              type="checkbox"
              checked={selectedTasks.has(task.id)}
              onChange={() => toggleTask(task.id)}
            />
            {task.label}
          </label>
        ))}
      </div>
      <button
        disabled={selectedTasks.size === 0}
        onClick={() => setStep(3)}
        className="w-full font-bold py-2 text-xs disabled:opacity-40"
        style={{ background: '#3b5998', color: 'white' }}
      >
        Next: Branding
      </button>
    </div>
  );

  // ── Step 3: Business Name ─────────────────────────────────────────────────

  const renderStep3 = () => (
    <div className="space-y-4">
      <label className="block text-xs font-bold" style={{ color: 'var(--text-secondary)' }}>
        3. Business Name
      </label>
      <input
        type="text"
        value={businessName}
        onChange={e => setBusinessName(e.target.value)}
        placeholder="e.g. Danny's HVAC"
        className="w-full border p-2 text-sm"
        style={{ borderColor: '#d1d5db' }}
      />
      <button
        disabled={!businessName.trim()}
        onClick={() => setStep(4)}
        className="w-full font-bold py-2 text-xs disabled:opacity-40"
        style={{ background: '#3b5998', color: 'white' }}
      >
        Next: Hours
      </button>
    </div>
  );

  // ── Step 4: Business Hours ────────────────────────────────────────────────

  const renderStep4 = () => (
    <div className="space-y-4">
      <label className="block text-xs font-bold" style={{ color: 'var(--text-secondary)' }}>
        4. Business Hours
      </label>
      <div className="flex gap-2 text-xs items-center">
        <input
          type="time"
          value={hoursStart}
          onChange={e => setHoursStart(e.target.value)}
          className="border p-1"
          style={{ borderColor: '#d1d5db' }}
        />
        <span>to</span>
        <input
          type="time"
          value={hoursEnd}
          onChange={e => setHoursEnd(e.target.value)}
          className="border p-1"
          style={{ borderColor: '#d1d5db' }}
        />
      </div>
      <button
        onClick={() => setStep(5)}
        className="w-full font-bold py-2 text-xs"
        style={{ background: '#4b8b3b', color: 'white' }}
      >
        Build Agent ($49)
      </button>
    </div>
  );

  // ── Step 5: Animation ─────────────────────────────────────────────────────

  const renderStep5 = () => (
    <div className="text-center py-10">
      <div className="text-4xl mb-4" style={{ animation: 'spin 2s linear infinite', display: 'inline-block' }}>
        ⚙️
      </div>
      <div className="font-bold text-lg mb-2">Building Custom Logic...</div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Injecting Industry Knowledge... Configuring Voice...
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // ── Step 6: Success ───────────────────────────────────────────────────────

  const renderStep6 = () => (
    <div className="text-center">
      <h2 className="text-xl font-bold mb-2" style={{ color: '#16a34a' }}>
        {businessName || 'Your'} Bot is LIVE!
      </h2>
      <div
        className="border rounded p-3 text-left mb-4 flex gap-3"
        style={{ borderColor: '#d1d5db', background: '#f9fafb' }}
      >
        <div
          className="w-12 h-12 flex items-center justify-center text-xl rounded"
          style={{ background: '#2563eb', color: 'white' }}
        >
          🔧
        </div>
        <div>
          <div className="font-bold text-sm">{businessName || 'MyBot'}</div>
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {industry} • {hoursStart}-{hoursEnd}
          </div>
        </div>
      </div>
      <button
        onClick={() => navigate('/')}
        className="font-bold px-4 py-2 text-xs rounded"
        style={{ background: '#3b5998', color: 'white' }}
      >
        Go to Dashboard
      </button>
    </div>
  );

  return (
    <div className="social-box p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold" style={{ color: '#29487d' }}>Guided Agent Build</h2>
        <span
          className="px-2 py-1 font-bold"
          style={{
            background: '#ffd700',
            color: '#29487d',
            fontSize: '10px',
          }}
        >
          $49 One-Time
        </span>
      </div>
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
      {step === 5 && renderStep5()}
      {step === 6 && renderStep6()}
    </div>
  );
}
